// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// convexLiveSync — pure decision helper for the convexOnly live→render-state
// sync (#326).
//
// In convexOnly mode the Convex websocket pushes fresh data into the TanStack
// cache (via ConvexBridge), but the grid renders from intermediate useState
// (projectListAfterMerge, awsgwAttributesResult, awsgwCandidatesResult) that was
// historically refreshed by the now-disabled 5-second poll. These states must be
// driven directly from the live subscription instead.
//
// This module holds the PURE decision — "given the current mode/modal state and
// an incoming live result, should we overwrite the render-state, and with what?"
// — with NO mutation and NO side effects, so the exact production logic is
// unit-tested (CLAUDE.md change-detection extraction rule; #73/#80/#83).
//
// Safety invariant (data-loss prevention): the optimistic mutation handlers in
// modelCx.context.js assume polling is paused while a modal is open ("Polling is
// paused when CrewDetailModal is open, so no race condition" — L4659/L4695). The
// Convex subscription is NOT paused by modals, so we replicate that invariant
// here: never overwrite render-state while ANY modal is open. On modal close the
// effect re-runs and applies the latest live data, which already reflects the
// user's own write (convexOnly writes to Convex first → subscription is
// newer-wins by construction).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Decide whether an incoming live-subscription result should replace the current
 * render-state, and what the next render-state is.
 *
 * @param {Object} p
 * @param {boolean} p.isConvexOnly    - true only for convexOnly users
 * @param {boolean} p.isAnyModalOpen  - true if ANY modal is open (mirror poll pause)
 * @param {*}       p.incoming        - the live result (array), or undefined/null while loading
 * @param {Array}   p.current         - the current render-state array
 * @returns {{ apply: boolean, next: Array, reason: string }}
 */
export function nextRenderState({ isConvexOnly, isAnyModalOpen, incoming, current }) {
  const cur = Array.isArray(current) ? current : [];

  // mongoOnly / both: this sync is inert — the Lambda poll path owns the state.
  if (!isConvexOnly) return { apply: false, next: cur, reason: "not-convexOnly" };

  // Mirror the polling-pause invariant: never clobber an in-flight optimistic
  // edit. The effect re-runs on modal close and applies the latest live data.
  if (isAnyModalOpen) return { apply: false, next: cur, reason: "modal-open" };

  // Still loading / re-subscribing (e.g. right after a location change): skip.
  // A loaded-but-empty office is `[]` (applied below); loading is undefined/null.
  if (incoming === undefined || incoming === null) {
    return { apply: false, next: cur, reason: "loading" };
  }
  if (!Array.isArray(incoming)) return { apply: false, next: cur, reason: "not-array" };

  // Defensive: do not blank a populated grid with a transient empty result.
  // A genuinely-empty office self-corrects on the next push / reload. This
  // matches the pre-existing poll guard (modelCx L6602 "keep existing").
  if (incoming.length === 0 && cur.length > 0) {
    return { apply: false, next: cur, reason: "empty-incoming-keep-existing" };
  }

  return { apply: true, next: incoming, reason: "applied" };
}
